/**
 * Centralized credentials and configuration
 * All environment variables are accessed through this file
 */
import { clientLogger } from '../utils/clientLogger'

// Supabase Configuration
export const supabaseConfig = {
  url: import.meta.env.VITE_SUPABASE_URL || '',
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  // NOTE: Service role key should NEVER be in client code
  // Only use on backend with proper environment variables
}

// Backend API Configuration
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_BASE_URL
  const defaultUrl = 'http://127.0.0.1:3005'

  // In development, allow http://localhost
  if (import.meta.env.MODE === 'development') {
    return envUrl || defaultUrl
  }

  // In production, enforce HTTPS
  if (envUrl && !envUrl.startsWith('https://')) {
    throw new Error('Production API must use HTTPS')
  }

  return envUrl || ''
}

export const apiConfig = {
  baseUrl: getApiBaseUrl(),
  timeout: 10000, // 10 seconds
}

// NOTE: All API keys and secrets should be handled by the backend.
// The client should only communicate with the backend, which proxies external APIs.
// Only PUBLIC keys that are safe to expose in the browser should be here.

// OpenAI Configuration (client-side model info only)
export const openaiConfig = {
  model: 'gpt-4o-mini', // Cost-optimized model
  // API keys are on backend only - client communicates via apiConfig.baseUrl
}

// Kroger Configuration (client-side config only)
export const krogerConfig = {
  baseUrl: 'https://api.kroger.com', // For reference only
  // Client credentials are on backend only - client uses backend proxy
}

// Monitoring Configuration (public DSN is safe to expose)
export const monitoringConfig = {
  sentry: {
    dsn: import.meta.env.VITE_SENTRY_DSN || '', // Public DSN is safe
    org: 'talktoast',
    project: 'talktoast-client',
  }
}

// Subscription Management Configuration (public key only)
export const subscriptionConfig = {
  revenueCat: {
    publicApiKey: import.meta.env.VITE_REVENUECAT_PUBLIC_API_KEY || '',
    // Secret key is on backend only
  }
}

// App Configuration
export const appConfig = {
  env: import.meta.env.VITE_ENV || 'development',
  debug: import.meta.env.DEBUG === 'true',
  version: '0.0.1',
  name: 'TalkToast App',
}

// Cache TTL Configuration (in seconds)
export const cacheConfig = {
  recipes: 24 * 60 * 60, // 24 hours
  mealPlans: 30 * 24 * 60 * 60, // 30 days minimum
  favorites: -1, // Indefinite (no expiry)
  instacartPrices: 0, // No cache - always fresh
  userPreferences: 7 * 24 * 60 * 60, // 1 week
}

// Validation helper
export const validateCredentials = () => {
  const errors: string[] = []
  const warnings: string[] = []

  // Required credentials for all environments
  const required = [
    { key: 'VITE_SUPABASE_URL', value: supabaseConfig.url, name: 'Supabase URL' },
    { key: 'VITE_SUPABASE_ANON_KEY', value: supabaseConfig.anonKey, name: 'Supabase Anon Key' },
  ]

  // Check for missing required credentials
  required.forEach(({ key, value, name }) => {
    if (!value) {
      errors.push(`${name} (${key}) is required`)
    } else if (value.includes('mock') || value.includes('your-project-id')) {
      warnings.push(`${name} appears to be a placeholder value`)
    }
  })

  // Check API base URL configuration
  if (!apiConfig.baseUrl) {
    if (appConfig.env === 'production') {
      errors.push('API base URL must be configured in production')
    } else {
      warnings.push('API base URL not configured, using default')
    }
  }

  // In production, enforce stricter validation
  if (appConfig.env === 'production') {
    if (!supabaseConfig.url.startsWith('https://')) {
      errors.push('Supabase URL must use HTTPS in production')
    }
    if (apiConfig.baseUrl && !apiConfig.baseUrl.startsWith('https://')) {
      errors.push('API base URL must use HTTPS in production')
    }
  }

  // Log warnings
  if (warnings.length > 0) {
    clientLogger.warn({ warnings }, 'Configuration warnings')
  }

  // Throw errors in production, warn in development
  if (errors.length > 0) {
    const errorMessage = `Configuration errors:\n${errors.map(e => `  - ${e}`).join('\n')}`

    if (appConfig.env === 'production') {
      throw new Error(errorMessage)
    } else {
      clientLogger.error({ errors }, 'Configuration errors')
    }
  }

  return errors.length === 0
}